import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gaEvent } from '@/lib/analytics';

export const DEMO_VIDEO_LD = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'DotLink in 30 seconds: scan, call, done',
  description: 'A car blocks yours. Scan the DotLink QR sticker, call the owner, and your number stays private.',
  thumbnailUrl: 'https://dotlinkapp.com/videos/dotlink-demo-poster.jpg',
  uploadDate: '2026-10-16',
  duration: 'PT31S',
  contentUrl: 'https://dotlinkapp.com/videos/dotlink-demo.mp4',
};

const POINTS = [
  ['Scan', 'Point any phone camera at the sticker. No app to install.'],
  ['Call or message', 'The owner is notified instantly, with a ringing call or a chat.'],
  ['Number stays private', 'Neither side ever sees the other’s phone number.'],
];

// Phone-framed product video: silent, loops, and plays only while it is on screen
// (saves data and battery). Visitors who prefer reduced motion get the cover image and a play button.
const DemoVideo = ({ className = '', showCta = true }) => {
  const ref = useRef(null);
  const box = useRef(null);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const reduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const userPaused = useRef(reduced);

  useEffect(() => {
    const v = ref.current;
    if (!v || !box.current) return undefined;
    const start = () => { if (!userPaused.current) v.play().then(() => setPaused(false)).catch(() => {}); };
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { start(); } else { v.pause(); }
    }, { threshold: 0.4 });
    io.observe(box.current);
    const vis = () => { if (document.hidden) v.pause(); };
    document.addEventListener('visibilitychange', vis);
    return () => { io.disconnect(); document.removeEventListener('visibilitychange', vis); };
  }, []);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { userPaused.current = false; v.play().then(() => { setPaused(false); gaEvent('demo_video_play', { source: window.location.pathname }); }).catch(() => {}); }
    else { userPaused.current = true; v.pause(); setPaused(true); }
  };

  return (
    <section className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900 text-white ${className}`} aria-labelledby="demo-video-title">
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-indigo-500/30 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -right-16 w-80 h-80 rounded-full bg-purple-500/30 blur-3xl" aria-hidden="true" />
      <div className="relative grid md:grid-cols-2 gap-10 items-center p-8 sm:p-12">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] text-indigo-200 mb-3">SEE IT IN ACTION</p>
          <h2 id="demo-video-title" className="text-3xl sm:text-4xl font-extrabold leading-tight">Blocked in? Scan, call, done.</h2>
          <p className="text-indigo-100 mt-4 text-lg">Thirty seconds that show how a DotLink sticker connects a stranger to the owner, without a single phone number changing hands.</p>
          <ul className="mt-6 space-y-4">
            {POINTS.map(([t, d], i) => (
              <li key={t} className="flex gap-4">
                <span className="shrink-0 w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-bold text-indigo-100">{i + 1}</span>
                <span><b className="block">{t}</b><span className="text-sm text-indigo-200">{d}</span></span>
              </li>
            ))}
          </ul>
          {showCta && (
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="btn-press rounded-full bg-white text-indigo-900 font-semibold px-6 py-3 hover:shadow-xl transition-shadow">Try the live demo</Link>
              <Link to="/register" className="btn-press rounded-full border border-white/40 text-white font-semibold px-6 py-3 hover:bg-white/10 transition-colors">Get started free</Link>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          <div ref={box} className="relative w-[250px] sm:w-[290px]">
            <div className="absolute inset-0 rounded-[2.6rem] bg-gradient-to-br from-indigo-400 to-purple-500 blur-2xl opacity-40 animate-pulse" aria-hidden="true" />
            <button type="button" onClick={toggle} aria-label={paused ? 'Play the DotLink demo' : 'Pause the DotLink demo'}
              className="relative block w-full rounded-[2.6rem] border-[9px] border-gray-950 bg-black shadow-2xl overflow-hidden aspect-[9/16] group focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300">
              <span className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-gray-950 z-20" aria-hidden="true" />
              <video
                ref={ref}
                className="w-full h-full object-cover"
                src="/videos/dotlink-demo.mp4"
                poster="/videos/dotlink-demo-poster.jpg"
                muted
                loop
                playsInline
                preload="metadata"
                onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime / (e.currentTarget.duration || 31))}
                aria-label="Silent DotLink demo: scan the sticker, call the owner, number stays private"
              />
              {paused && (
                <span className="absolute inset-0 z-10 flex items-center justify-center bg-indigo-950/30">
                  <span className="w-20 h-20 rounded-full bg-white shadow-2xl flex items-center justify-center">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="#4338ca" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                  </span>
                </span>
              )}
              <span className="absolute left-5 right-5 bottom-3 h-1 rounded-full bg-white/20 z-10" aria-hidden="true">
                <span className="block h-full rounded-full bg-gradient-to-r from-indigo-300 to-purple-300" style={{ width: `${Math.round(progress * 100)}%` }} />
              </span>
            </button>
            <p className="flex items-center justify-center gap-2 text-xs text-indigo-200 mt-4">
              <span className={`w-2 h-2 rounded-full ${paused ? 'bg-indigo-300' : 'bg-emerald-400 animate-pulse'}`} aria-hidden="true" />
              {paused ? 'Paused · tap to play' : 'Live preview · tap to pause'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoVideo;
